import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Coins } from "lucide-react";

import { authAxios } from "../utils/auth";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";

/**
 * CoinGateContext — global "Out of Coins" gate.
 *
 * Exposes a single async helper via `useCoinGate()`:
 *
 *   const { requireCoins } = useCoinGate();
 *   const handleAction = async () => {
 *     if (!(await requireCoins())) return;
 *     // …fire the LLM-costing request
 *   };
 *
 * `requireCoins` fetches a fresh balance from /api/credits/balance and:
 *   • returns `true` if balance > 0 → caller proceeds normally
 *   • otherwise opens the shared "Out of Coins" modal and returns `false`
 *     → caller aborts silently, user sees BUY COINS CTA
 *
 * The endpoint is O(1) with the atomic $inc counter in add_transaction —
 * one call per gated action is ~100 ms and immune to reconciliation races.
 *
 * Fails open on network error: the backend pre_flight_balance_check on
 * /api/ai/chat and /api/eroscoach/message is the second line of defence.
 */

const CoinGateContext = createContext(null);

export function CoinGateProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [balance, setBalance] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const refreshBalance = useCallback(async () => {
    try {
      const res = await authAxios.get("/credits/balance");
      setBalance(Number(res?.data?.balance ?? 0));
    } catch {
      // keep last-known value on transient error
    }
  }, []);

  // Fetch on mount + whenever the tab regains focus so button disabled
  // states stay reasonably fresh without polling.
  useEffect(() => {
    refreshBalance();
    const onFocus = () => refreshBalance();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshBalance]);

  // Global 402 OUT_OF_COINS event bridge — the axios response interceptor
  // in utils/auth.js dispatches `eros:out-of-coins` for any endpoint that
  // 402's with the OUT_OF_COINS code. We open the shared modal here so
  // future backend routes automatically get the same UX without any
  // per-caller wiring.
  useEffect(() => {
    const onOutOfCoins = () => {
      setBalance(0);
      setOpen(true);
    };
    window.addEventListener("eros:out-of-coins", onOutOfCoins);
    return () => window.removeEventListener("eros:out-of-coins", onOutOfCoins);
  }, []);

  const goToTopup = () => {
    setOpen(false);
    const from = `${location.pathname}${location.search}${location.hash}`;
    navigate("/plans?tab=topup", { state: { from } });
  };

  const requireCoins = useCallback(async () => {
    try {
      const res = await authAxios.get("/credits/balance");
      const bal = Number(res?.data?.balance ?? 0);
      setBalance(bal);
      if (bal > 0) return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[CoinGate] balance check failed, allowing call through:", err?.message || err);
      return true;
    }
    setOpen(true);
    return false;
  }, []);

  return (
    <CoinGateContext.Provider value={{
      requireCoins,
      openOutOfCoinsModal: () => setOpen(true),
      balance,
      refreshBalance,
    }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-divine-black-light border-white/10 sm:max-w-sm"
          data-testid="out-of-coins-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-white text-lg font-medium flex items-center gap-2">
              <Coins className="w-5 h-5 text-amber-400" />
              Out of Coins
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-white/70 leading-relaxed">
            Your coin balance requires a topup. Purchase more coins, or subscribe to continue.
          </p>
          <DialogFooter className="mt-2 gap-2 sm:gap-2">
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              className="text-white/60 hover:text-white hover:bg-white/5"
              data-testid="out-of-coins-dismiss-btn"
            >
              Not now
            </Button>
            <Button
              onClick={goToTopup}
              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold tracking-wide"
              data-testid="out-of-coins-buy-btn"
            >
              BUY COINS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CoinGateContext.Provider>
  );
}

export function useCoinGate() {
  const ctx = useContext(CoinGateContext);
  if (!ctx) {
    // No provider — fail open so tests / storybook / island components
    // don't have to mount the whole app shell.
    return {
      requireCoins: async () => true,
      openOutOfCoinsModal: () => {},
      balance: null,
      refreshBalance: async () => {},
    };
  }
  return ctx;
}

/**
 * Convenience hook — returns just the current (cached) balance. Use to drive
 * `disabled` state on gated buttons so users see the friction before clicking.
 * Returns `null` until the first balance fetch resolves.
 */
export function useCoinBalance() {
  return useCoinGate().balance;
}
